/**
 * Session and re-authentication policy — Document 6 §2 (D-10),
 * Architecture §6.3 and §6.6.
 */

/** Cookie name. Prefixed so it is obvious which product owns it. */
export const SESSION_COOKIE_NAME = 'pp_session';

/**
 * 30 days, refreshed on activity.
 *
 * Long because a studio keeps one authenticated session open on a shared
 * tablet all day (PRD §3.2); a short expiry would put a login screen between
 * an instructor and a measurement several times a week. The protection that
 * matters here is not a short window but the admin re-auth gate on everything
 * destructive.
 */
export const SESSION_LIFETIME_SECONDS = 30 * 24 * 60 * 60;

/**
 * Only extend the stored expiry when the session is older than this, so a busy
 * tablet does not write to the sessions row on every single request.
 */
export const SESSION_REFRESH_THRESHOLD_SECONDS = 60 * 60;

/** Admin re-auth window. Absolute, never extended by activity. */
export const ADMIN_REAUTH_WINDOW_SECONDS = 10 * 60;

/** Failed attempts before the studio credential locks. */
export const LOGIN_MAX_FAILED_ATTEMPTS = 10;

/** How long the lock lasts once tripped. */
export const LOGIN_LOCKOUT_SECONDS = 15 * 60;

/**
 * The Pilates Progress admin panel — added 11 September 2026.
 *
 * A separate cookie, on purpose: this account can list, create and suspend
 * every studio, which no studio session can do and none should be able to
 * reach for by confusing one cookie with the other. A distinct name is the
 * first line of that separation; the account, session and middleware are all
 * separate tables and code paths behind it (migration 0017).
 */
export const PLATFORM_SESSION_COOKIE_NAME = 'pp_admin_session';

/**
 * Short relative to a studio's 30 days. This account is used occasionally, by
 * one person today, and can touch every studio — a session worth re-entering
 * a password for more often than a shared tablet's.
 */
export const PLATFORM_SESSION_LIFETIME_SECONDS = 24 * 60 * 60;

export const PLATFORM_SESSION_REFRESH_THRESHOLD_SECONDS = 15 * 60;

/** Same throttling shape as a studio login — Doc 8 §Security Tasks. */
export const PLATFORM_LOGIN_MAX_FAILED_ATTEMPTS = 10;
export const PLATFORM_LOGIN_LOCKOUT_SECONDS = 15 * 60;

/** Audit event types recorded in Sprint 1. Database Schema §16. */
export const AUDIT_EVENTS = {
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  ADMIN_REAUTH_SUCCESS: 'ADMIN_REAUTH_SUCCESS',
  ADMIN_REAUTH_FAILURE: 'ADMIN_REAUTH_FAILURE',
  INSTRUCTOR_CREATED: 'INSTRUCTOR_CREATED',
  INSTRUCTOR_DEACTIVATED: 'INSTRUCTOR_DEACTIVATED',
  INSTRUCTOR_PASSWORD_RESET: 'INSTRUCTOR_PASSWORD_RESET',
  PASSWORD_CHANGED: 'PASSWORD_CHANGED',
  STUDENT_REASSIGNED: 'STUDENT_REASSIGNED',
  STUDENT_CREATED: 'STUDENT_CREATED',
  STUDENT_ARCHIVED: 'STUDENT_ARCHIVED',
  STUDENT_RESTORED: 'STUDENT_RESTORED',
  STUDENT_PERMANENTLY_DELETED: 'STUDENT_PERMANENTLY_DELETED',
  REPORT_DELETED: 'REPORT_DELETED',
  STUDIO_SETTINGS_CHANGED: 'STUDIO_SETTINGS_CHANGED',
  STUDIO_LOGO_CHANGED: 'STUDIO_LOGO_CHANGED',
  STUDIO_CREATED: 'STUDIO_CREATED',
  STUDIO_SUSPENDED: 'STUDIO_SUSPENDED',
  STUDIO_REACTIVATED: 'STUDIO_REACTIVATED',
  STUDIO_OWNER_PASSWORD_RESET: 'STUDIO_OWNER_PASSWORD_RESET',
} as const;

export type AuditEventType = (typeof AUDIT_EVENTS)[keyof typeof AUDIT_EVENTS];

/**
 * Who a session belongs to.
 *
 * Until 6 September 2026 a studio had one password and instructor selection
 * was attribution only — every instructor could reach every student. The
 * product owner asked for a real boundary, so an account now has a role and
 * the role decides what it can see.
 */
export const ROLES = ['owner', 'instructor'] as const;
export type Role = (typeof ROLES)[number];

/**
 * The studio owner's username inside their own studio.
 *
 * Everyone signs in with the same three fields — studio code, username,
 * password — so the login form has one shape. This is the default the owner
 * gets; it lives in `studio_credentials.owner_username_normalized`, so a
 * studio that wants a different word is a row update rather than a release.
 * Instructors may not take it.
 */
export const DEFAULT_OWNER_USERNAME = 'yonetici';

/**
 * The studio owner's password, which we issue and the studio keeps.
 *
 * This one guards manager mode, instructor accounts and every destructive
 * action, so it stays long. It is typed rarely and by one person.
 */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * An instructor's password is a six-digit PIN — revised 10 September 2026.
 *
 * It replaced a long generated passphrase that instructors had to be told and
 * then immediately made to replace. Two things were wrong with that. The
 * passphrase was unusable on the shared tablet an instructor actually signs in
 * on, several times a day, in front of a student. And the replacement step
 * assumed the first password was a secret in transit, which it never was: the
 * owner reads it out loud either way.
 *
 * So the PIN is the password. There is no first-use change, because the owner
 * is the one who hands it over and the one who resets it when it is forgotten.
 *
 * Six digits is a million combinations, which is only enough because guessing
 * is not free: LOGIN_MAX_FAILED_ATTEMPTS wrong tries lock the account for
 * LOGIN_LOCKOUT_SECONDS, so an attacker gets forty attempts an hour against a
 * visibly locked account. It would be the wrong choice for anything reachable
 * by a stranger; for a studio's own tablet it is the right one.
 */
export const INSTRUCTOR_PIN_LENGTH = 6;

/** Digits only, exactly INSTRUCTOR_PIN_LENGTH of them. */
export function isInstructorPin(value: string): boolean {
  if (value.length !== INSTRUCTOR_PIN_LENGTH) return false;
  // Not a built regex: '\d' inside a template literal is the letter d, which
  // makes `^\d{6}$` match 'dddddd' and nothing else. Caught by the linter
  // rather than by a test, which is the wrong way round to find it.
  return [...value].every((character) => character >= '0' && character <= '9');
}

/**
 * Generated, never chosen.
 *
 * A person picking six digits picks a birthday or 123456, and there are few
 * enough of those to try inside one lockout window. Rejection sampling keeps
 * every PIN equally likely — the modulo shortcut would favour the low end.
 */
export function generateInstructorPin(): string {
  let pin = '';
  while (pin.length < INSTRUCTOR_PIN_LENGTH) {
    const byte = crypto.getRandomValues(new Uint8Array(1))[0] as number;
    if (byte < 250) pin += String(byte % 10);
  }
  return pin;
}

/**
 * A studio owner's password — the same shape scripts/create-studio.ts always
 * generated, moved here so the admin panel produces an identical-looking
 * password through a second door rather than a differently-shaped one.
 * Readable but high-entropy, from an alphabet without characters that are
 * misheard: this is going to be read out loud to a studio owner.
 *
 * Shortened from 4 groups of 5 to 3 groups of 4 on 11 September 2026 — the
 * product owner found the original too long to read out or type comfortably.
 * 12 characters from this 32-symbol alphabet is still 60 bits, and the actual
 * ceiling on a guessed password is the same one that made a six-digit
 * instructor PIN acceptable: ten wrong attempts lock the account for fifteen
 * minutes (LOGIN_MAX_FAILED_ATTEMPTS, LOGIN_LOCKOUT_SECONDS). A password far
 * beyond that ceiling costs usability and buys nothing.
 */
export function generateStudioPassword(): string {
  const alphabet = 'abcdefghjkmnpqrstuvwxyz23456789';
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  const chars = [...bytes].map((byte) => alphabet[byte % alphabet.length]);
  return [0, 4, 8].map((i) => chars.slice(i, i + 4).join('')).join('-');
}

/**
 * Turns a name into a username someone can type at a login screen.
 *
 *   "Ayşe Yılmaz"  ->  "ayseyilmaz"
 *
 * Typing a Turkish name into the username box is the obvious thing to do, and
 * it used to be rejected with a validation error naming no field. Folding as
 * the owner types means an invalid username cannot be entered at all, and they
 * see exactly what the instructor will have to type.
 */
export function toUsername(value: string): string {
  return value
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[âîû]/g, (c) => ({ â: 'a', î: 'i', û: 'u' })[c] ?? c)
    .replace(/[^a-z0-9]/g, '');
}
